import dayjs from 'dayjs';
import isInt from 'validator/lib/isInt';
import isEmail from 'validator/lib/isEmail';
import isDate from 'validator/lib/isDate';
import isFloat from 'validator/lib/isFloat';
import {
  isNil,
  isEmpty,
  trim,
  toString,
  toInteger,
  keys,
  filter,
  get,
  isBoolean,
  toNumber,
} from 'lodash/fp';
import securityNumberValidator, { isITIN } from './securityNumber';
import socialInsuranceNumberValidator from './socialInsuranceNumber';
import dateUtils from './date';
import { validateVIN } from './vin';
import { isName, isLongName, isPhone } from './common';

const datePattern = /^([0-9]|0[1-9]|1[0-2])\/([0-9]|[0-2][0-9]|3[0-1])\/[0-9]{4}$/;

const datePatternDayFirst = /^([0-9]|[0-2][0-9]|3[0-1])\/([0-9]|[0-1][0-2])\/[0-9]{4}$/;
const datePatternYearFirst = /^[0-9]{4}\/[0-9]{2}\/[0-9]{2}$/;

const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
const jobTitlePattern = /^[\w\d]+[\w\d\s.&()*[\]/+’',-;#]*$/;
const employerPattern = /^[\w\d]+[\w\d\s.&()*[\]/’',-;#]*$/;
const addressPattern = /^[a-zA-ZÀ-ú0-9\s,'-]*$/;

// Simplest attempt at a match of the following PCRE regex /\A[\p{L}\d]+[\p{L}\d\s.&()\*\[\]\/'',-;#]*\z/
// The \p{L} expression includes letters from any language, so the easy route here is to explicitly exclude any
// specific special chars while allowing the passing of unspecified unicode chars
const upstartSchoolNamePattern = /^[^-!$%^&*()_+|~=`{}[\]:/;<>?,.@#'"\u2026\s][^{}!@$%^_+=\\"|`~\u2026]*$/;

// eslint-disable-next-line max-len
const vinPattern = /^[A-HJ-NPR-Z\d]{3}[A-HJ-NPR-Z\d]{5}[\dX][A-HJ-NPR-Z\d][A-HJ-NPR-Z\d][A-HJ-NPR-Z\d]{6}$/;
export const MIN_YEAR = 1920;
const MAX_SALARY = 999999;
const MAX_ANNUAL_INCOME = MAX_SALARY * 12;
const MAX_HOURS_PER_WEEK = 168;
const MAX_OTHER_INCOME_SOURCE_LENGTH = 13;
const MAX_PAY_OFF = 999999;
export const MIN_AGE = 16;
export const MAX_AGE = 100;

export const validateOr = <T = unknown>(
  validatorFunctions: ((
    value: T,
    formData: unknown,
  ) => boolean | Promise<boolean>)[],
) => async (value: T, formData: unknown) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const func of validatorFunctions) {
    // eslint-disable-next-line no-await-in-loop
    if (await func(value, formData)) return true;
  }
  return false;
};

const checkIsEmptyString = (value: string | undefined | null) =>
  isNil(value) || value === '';

export const optional = (
  validatorFunction: (
    value: string,
    formData: unknown,
  ) => boolean | Promise<boolean>,
) => validateOr([checkIsEmptyString, validatorFunction]);

const checkDate = (date: string | undefined | null): date is string =>
  !isNil(date) &&
  isDate(date, { strictMode: true, format: 'MM/DD/YYYY' }) &&
  datePattern.test(date) &&
  dayjs(date).year() >= MIN_YEAR;

const checkBirthday = (date: string | undefined | null): date is string =>
  checkDate(date) && dateUtils.getYearsDiff(date) >= MIN_AGE;

const checkDateYearFirst = (date: string | undefined | null): date is string =>
  !isNil(date) &&
  isDate(date) &&
  datePatternYearFirst.test(date) &&
  dayjs(date, 'YYYY/MM/DD').year() >= MIN_YEAR;

const checkDateDayFirst = (date: string | undefined | null): date is string =>
  !isNil(date) &&
  datePatternDayFirst.test(date) &&
  dayjs(date, 'DD/MM/YYYY').year() >= MIN_YEAR;

const checkIsFullName = (form: any) => (value: string) => {
  if (!isEmpty(value)) {
    // all good if name field has a value
    return true;
  }
  // determine which field we're checking and make sure it's counterpart is also empty
  const emptyField = isEmpty(form.firstName) ? form.firstName : form.lastName;
  const counterpart =
    emptyField === form.firstName ? form.lastName : form.firstName;
  return isEmpty(counterpart);
};

const checkBirthdayYearFirst = (
  date: string | undefined | null,
): date is string =>
  checkDateYearFirst(date) && dateUtils.getYearsDiffYearFirst(date) >= MIN_AGE;

const checkBirthdayDayFirst = (
  date: string | undefined | null,
): date is string =>
  checkDateDayFirst(date) && dateUtils.getYearsDiffDayFirst(date) >= MIN_AGE;

const validEmailSyntax = (email: string | undefined | null): email is string =>
  !isNil(email) &&
  isEmail(email) &&
  emailPattern.test(email) &&
  email.length <= 50;

const checkEmail = (email: string) => {
  const trimmedEmail = trim(email);
  return validEmailSyntax(trimmedEmail);
};

// eslint-disable-next-line no-control-regex
const isASCII = (str: string) => /^[\x00-\x7F]*$/.test(str);

const isAlphanum = (str: string) => /^[a-z0-9]+$/i.test(str);

const checkIsNotEmpty = (value: string | undefined | null): value is string =>
  !isNil(value) && trim(value) !== '';

const checkAddress = (address: string | undefined | null): address is string =>
  !isEmpty(address) &&
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  address!.length <= 50 &&
  addressPattern.test(<string>address);

const checkCity = (city: string | undefined | null): city is string =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  !isEmpty(city) && city!.length > 1 && city!.length <= 38;

const checkExtendedZip = (number?: number | string | null) =>
  !isEmpty(toString(number).match(/^\d{5}-\d+$/));

const checkState = (state: string | undefined | null): state is string =>
  !isEmpty(state);

const checkFullAddress = (arg: {
  address?: string;
  city?: string;
  zip?: string;
  state?: string;
}) =>
  checkAddress(arg.address) &&
  checkCity(arg.city) &&
  checkExtendedZip(arg.zip) &&
  checkState(arg.state);

const checkOtherIncomeSource = (str: any) => {
  let value = str || '';
  value = value.replace(/\s/g, '');
  return (
    !isEmpty(value) &&
    isAlphanum(value) &&
    str.length <= MAX_OTHER_INCOME_SOURCE_LENGTH
  );
};

const zipValidator = (value: string | undefined | null): value is string => {
  // matches a us zipcode, like '94108' or '20521-9000'
  const usZipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  // matches a canadian postal code, like 'H0H 0H0'
  const caPostalRegex = /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/;
  return (
    !!value &&
    value.length !== 0 &&
    !usZipRegex.test(value) &&
    !caPostalRegex.test(value)
  );
};

const checkNaturalNumber = (number: any) => isInt(toString(number), { min: 0 });

const checkYearWithinRange = (minYear: number, maxYear: number) => (
  number: any,
) => {
  if (checkNaturalNumber(number)) {
    const num = parseInt(number, 10);
    return num >= minYear && num <= maxYear;
  }
  return false;
};

const checkValidUpstartSchoolName = (schoolName: string | undefined | null) =>
  checkIsNotEmpty(schoolName) && upstartSchoolNamePattern.test(schoolName);

export default {
  matchWithRules: (
    rules: Record<string, (value: unknown) => boolean | Promise<boolean>>,
  ) => async (form: unknown) => {
    const fields = keys(rules);
    const result = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const field of fields) {
      const value = get(field)(form);
      // eslint-disable-next-line no-await-in-loop
      const isValid = await rules[field](value);
      result.push({ field, isValid });
    }
    const errors = filter(({ isValid }) => !isValid)(result);
    return isEmpty(errors) ? Promise.resolve() : Promise.reject(errors);
  },
  isName,
  isLongName,
  isEmail: checkEmail,
  isDate: checkDate,
  isDateYearFirst: checkDateYearFirst,
  isBirthday: checkBirthday,
  isBirthdayYearFirst: checkBirthdayYearFirst,
  isBirthdayDayFirst: checkBirthdayDayFirst,
  isPhone,
  isAddress: checkAddress,
  isNaturalNumber: checkNaturalNumber,
  isSalary: (number: number | null) =>
    isInt(toString(number), { min: 0 }) && toInteger(number) <= MAX_SALARY,
  isHourlyRate: (input: string) => {
    const pattern = /^([1-9][0-9]*|0)(\.[0-9]{1,2})?$/;
    return pattern.test(input);
  },
  isHoursPerWeek: (number: any) =>
    isInt(toString(number), { min: 1 }) &&
    toInteger(number) <= MAX_HOURS_PER_WEEK,
  isAnnualIncome: (number: number) =>
    isInt(toString(number), { min: 0 }) &&
    toInteger(number) <= MAX_ANNUAL_INCOME,
  isOtherIncome: (number: number | null) =>
    isInt(toString(number), { min: 1, max: MAX_SALARY }),
  isSecurityNumber: securityNumberValidator,
  isITIN,
  isSocialInsuranceNumber: socialInsuranceNumberValidator,
  isZip: zipValidator,
  isExtendedZip: checkExtendedZip,
  isEmployer: (employer: string | undefined | null): employer is string =>
    checkIsNotEmpty(employer) &&
    employer.length <= 100 &&
    employerPattern.test(employer),
  isCurrency: (currency: string) => /^\$?[0-9.,]{1,6}$/.test(currency),
  isJobTitle: (title: string | undefined | null): title is string =>
    checkIsNotEmpty(title) &&
    isASCII(title) &&
    title.length <= 100 &&
    jobTitlePattern.test(title),
  isCity: checkCity,
  isMileage: (mileage: any) =>
    isInt(toString(mileage), { min: 0 }) && mileage >= 1 && mileage <= 1000000,
  isNotEmpty: checkIsNotEmpty,
  isApt: (apt: any) => (apt ? isASCII(apt) && apt.length <= 60 : false),
  isDriverLicenseNumber: (num: any) =>
    !isEmpty(trim(num)) && isAlphanum(trim(num)) && trim(num).length <= 15,
  // TODO Consider expanding Canadian Validation by State as per the guidelines found here:
  // https://www.sonnet.ca/FAQ/Quoting/What-is-the-correct-way-to-enter-my-drivers-licence-information
  isMortgage: (number: number | null) =>
    isFloat(toString(number), { min: 0 }) && toNumber(number) <= 99999,
  isPayOff: (number: number) =>
    isFloat(toString(number), { min: 0 }) && toNumber(number) <= MAX_PAY_OFF,
  isBoolean,
  isVin: (vin: string) => vinPattern.test(trim(vin)),
  isVinStrict: (vin: string) => validateVIN(vin),
  isFullAddress: checkFullAddress,
  isFullName: checkIsFullName,
  isOtherIncomeSource: checkOtherIncomeSource,
  emailPattern,
  isYearWithinRange: checkYearWithinRange,
  isValidUpstartSchoolName: checkValidUpstartSchoolName,
};
